/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps } from '@mui/material';
import { Box, Grid, LinearProgress, useTheme } from '@mui/material';
import React from 'react';
import type {
  DraggablePosition,
  Position,
  DraggableSize,
  HeaderSize,
  Size,
} from '@opengeoweb/shared';
import {
  AlertBanner,
  calculateStartSize,
  ToolContainerDraggable,
  renderCounter,
  useWheelStopPropagation,
} from '@opengeoweb/shared';
import type {
  layerTypes,
  mapTypes,
  serviceTypes,
  uiTypes,
} from '@opengeoweb/store';
import BaseLayerRow from './BaseLayerRow/BaseLayerRow';
import DescriptionRow from './DescriptionRow/DescriptionRow';
import LayerContainerRow from './LayerContainerRow/LayerContainerRow';
import { sizeLarge, sizeMedium } from './LayerManagerHeaderOptions';
import HeaderOptionsConnect from './LayerManagerHeaderOptionsConnect';
import type { LayerManagerCustomSettings } from './LayerManagerUtils';
import { layerManagerStyle } from './LayerManagerUtils';
import OverlayersSelectConnect from './OverlayersRow/OverlayersSelectConnect';
import ProjectionSelectConnect from './ProjectionSelect/ProjectionSelectConnect';
import { useCoreTranslation } from '../../utils/i18n';

interface LayerManagerProps {
  mapId: string;
  preloadedAvailableBaseLayers?: layerTypes.Layer[];
  preloadedBaseServices?: serviceTypes.InitialService[];
  bounds?: string;
  title?: string;
  onClose: () => void;
  onMouseDown?: () => void;
  isOpen: boolean;
  order?: number;
  source?: uiTypes.Source;
  isLoading?: boolean;
  error?: string;
  isDockedLayerManager?: boolean;
  onToggleDock?: () => void;
  onDockedLayerManagerDragEnd?: (position: DraggablePosition) => void;
  onDockedLayerManagerSizeChange?: (
    size: mapTypes.DockedLayerManagerSize,
  ) => void;
  onDockedLayerManagerDimensionsChange?: (
    dimensions: mapTypes.DockedLayerManagerDimensions,
  ) => void;
  size?: DraggableSize;
  startDraggablePosition?: DraggablePosition;
  startPosition?: Position;
  settings?: LayerManagerCustomSettings;
  headerSize?: HeaderSize;
  addLayerComponent?: React.ReactElement;
  defaultCollapsedColumns?: Record<string, boolean>;
}
const styles = {
  layerRowContainer: {
    position: 'relative',
    width: '100%',
    height: 'calc(100% - 59px)',
    display: 'inline-block',
    padding: '0 6px',
  },
  layerManagerContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
};

const minSize = { width: 100, height: 126 };

const getLayerManagerSizeValue = (size: Size): string => {
  if (!size.width || typeof size.width !== 'number') {
    return 'sizeLarge';
  }

  if (size.width < sizeMedium.width) {
    return 'sizeSmall';
  }

  if (size.width < sizeLarge.width) {
    return 'sizeMedium';
  }

  return 'sizeLarge';
};

const LayerManager: React.FC<LayerManagerProps> = ({
  mapId,
  preloadedAvailableBaseLayers,
  preloadedBaseServices,
  bounds,
  onClose,
  title,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  isLoading = false,
  error,
  isDockedLayerManager = false,
  onToggleDock = (): void => {},
  onDockedLayerManagerDragEnd = (): void => {},
  onDockedLayerManagerSizeChange = (): void => {},
  onDockedLayerManagerDimensionsChange = (): void => {},
  size = sizeLarge,
  startDraggablePosition = { x: 0, y: 0 },
  startPosition = { top: 85, left: 50 },
  settings,
  headerSize = 'medium',
  addLayerComponent,
  defaultCollapsedColumns = {},
}: LayerManagerProps) => {
  const { t } = useCoreTranslation();
  const theme = useTheme();
  renderCounter.count(LayerManager.name);

  const nonEmptyTitle = title ?? t('layermanager-title');

  const startSizeCalc = calculateStartSize(minSize, size, startPosition);
  const [sizeInState, setSizeInState] =
    React.useState<DraggableSize>(startSizeCalc);
  const dataSize = getLayerManagerSizeValue(sizeInState);
  const wasOpenRef = React.useRef(isOpen);

  React.useLayoutEffect(() => {
    const isOpening = isOpen && !wasOpenRef.current;

    if (isDockedLayerManager && isOpening) {
      setSizeInState(calculateStartSize(minSize, size, startPosition));
    }

    wasOpenRef.current = isOpen;
  }, [isDockedLayerManager, isOpen, size, startPosition]);

  const [collapsedColumns, setCollapsedColumns] = React.useState<
    Record<string, boolean>
  >(defaultCollapsedColumns);

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  const handleDragEnd = React.useCallback(
    (_position: DraggablePosition, dragSize: Size): void => {
      if (isDockedLayerManager) {
        onDockedLayerManagerDragEnd(_position);
        onDockedLayerManagerSizeChange(
          getLayerManagerSizeValue(dragSize) as mapTypes.DockedLayerManagerSize,
        );
        onDockedLayerManagerDimensionsChange({
          width: dragSize.width as number,
          height: dragSize.height as number,
        });
      }
      if (dragSize !== sizeInState) {
        setSizeInState(dragSize as DraggableSize);
      }
    },
    [
      isDockedLayerManager,
      onDockedLayerManagerDimensionsChange,
      onDockedLayerManagerDragEnd,
      onDockedLayerManagerSizeChange,
      sizeInState,
    ],
  );

  const handleResizeStop = React.useCallback(
    (_event: unknown, _direction: unknown, node: HTMLElement): void => {
      const { offsetWidth: width, offsetHeight: height } = node;
      setSizeInState({ width, height });
      if (isDockedLayerManager) {
        onDockedLayerManagerSizeChange(
          getLayerManagerSizeValue({
            width,
            height,
          }) as mapTypes.DockedLayerManagerSize,
        );
        onDockedLayerManagerDimensionsChange({ width, height });
      }
    },
    [
      isDockedLayerManager,
      onDockedLayerManagerDimensionsChange,
      onDockedLayerManagerSizeChange,
    ],
  );

  const handleChangeSize = React.useCallback(
    ({ width }: { width: number }): void => {
      setSizeInState((prevSizeInState) => {
        const nextSizeInState = { ...prevSizeInState, width };
        if (isDockedLayerManager) {
          onDockedLayerManagerSizeChange(
            getLayerManagerSizeValue({
              ...nextSizeInState,
            }) as mapTypes.DockedLayerManagerSize,
          );
          onDockedLayerManagerDimensionsChange({
            width,
            height: nextSizeInState.height as number,
          });
        }
        return nextSizeInState;
      });
    },
    [
      isDockedLayerManager,
      onDockedLayerManagerDimensionsChange,
      onDockedLayerManagerSizeChange,
    ],
  );

  return (
    <ToolContainerDraggable
      bounds={bounds}
      className="layermanager"
      closeIcon={settings?.toolbar?.closeButton?.icon}
      data-size={dataSize}
      data-testid="layerManagerWindow"
      dragHandleIcon={settings?.toolbar?.dragHandle?.icon}
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onDragEnd={handleDragEnd}
      onMouseDown={onMouseDown}
      onResizeStop={handleResizeStop}
      order={order}
      resetPositionOnOpen={isDockedLayerManager}
      rightHeaderComponent={
        <HeaderOptionsConnect
          buttonSettings={settings?.toolbar?.controlButtonSettings}
          isDockedLayerManager={isDockedLayerManager}
          mapId={mapId}
          onChangeSize={handleChangeSize}
          onClickDockButton={onToggleDock}
        />
      }
      source={source}
      startDraggablePosition={startDraggablePosition}
      startPosition={startPosition}
      startSize={sizeInState}
      sx={
        {
          ...layerManagerStyle,
          // eslint-disable-next-line @typescript-eslint/no-misused-spread
          ...theme.palette.geowebColors.layerManager.root,
        } as SxProps
      }
      title={nonEmptyTitle}
    >
      <Box
        onKeyDown={(event): void => {
          event.stopPropagation();
        }}
        ref={stopWheelEventPropagationRef}
      >
        {!!isLoading && (
          <LinearProgress
            color="secondary"
            data-testid="loading-bar"
            sx={{ position: 'absolute', width: '100%' }}
          />
        )}
        <Box className="layerManagerContainer">
          {!!error && (
            <Box sx={{ padding: '0 6px' }}>
              <AlertBanner shouldClose title={error} />
            </Box>
          )}
          <Grid
            container
            data-testid="layerManagerRowContainer"
            sx={styles.layerRowContainer}
          >
            <DescriptionRow
              addLayerComponent={addLayerComponent}
              collapsedColumns={collapsedColumns}
              onToggleCollapsed={(name: string) =>
                setCollapsedColumns({
                  ...collapsedColumns,
                  [name]: !collapsedColumns[name],
                })
              }
              settings={settings?.header}
              source={source}
            />
            <LayerContainerRow
              collapsedColumns={collapsedColumns}
              mapId={mapId}
              settings={settings?.content}
            />
            <OverlayersSelectConnect mapId={mapId} />
            <BaseLayerRow
              mapId={mapId}
              preloadedAvailableBaseLayers={preloadedAvailableBaseLayers}
              preloadedServices={preloadedBaseServices}
              settings={settings?.footer}
              tooltip={t('layermanager-baselayers-tooltip')}
            />
            {!settings?.footer?.projection?.shouldHide && (
              <ProjectionSelectConnect mapId={mapId} />
            )}
          </Grid>
        </Box>
      </Box>
    </ToolContainerDraggable>
  );
};

export default LayerManager;
